import React from 'react';
import { Link } from 'react-router-dom';
import { FiFacebook, FiInstagram, FiLinkedin, FiMail, FiMapPin, FiPhone } from 'react-icons/fi';
import logoImage from '../../assets/images/brand/coolandcarelogo.png';
import { ourSolutionData } from '../../utils/ourSolutionData';
import { useTranslation } from 'react-i18next';
import { getSiteConfig } from '../../utils/siteConfig';

const FacebookIcon = FiFacebook as React.ComponentType<{ className?: string }>;
const LinkedinIcon = FiLinkedin as React.ComponentType<{ className?: string }>;
const InstagramIcon = FiInstagram as React.ComponentType<{ className?: string }>;
const PhoneIcon = FiPhone as React.ComponentType<{ className?: string }>;
const MailIcon = FiMail as React.ComponentType<{ className?: string }>;
const MapIcon = FiMapPin as React.ComponentType<{ className?: string }>;
const Footer: React.FC = () => {
  const { t } = useTranslation();
  const siteConfig = getSiteConfig();

  return (
    <footer className="bg-[#060E1A] text-white">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <img src={logoImage} alt="Cool & Care" className="h-10 w-auto mb-5" />
            <p className="text-white/40 text-sm leading-relaxed max-w-xs">
              {t('footerSection.companyDescription')}
            </p>
            <div className="flex gap-3 mt-6">
              <a
                href="https://www.facebook.com/share/1CBDzeqfWQ/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-white/5 hover:bg-teal-500 flex items-center justify-center transition-all duration-300 hover:scale-110"
              >
                <FacebookIcon className="w-4 h-4" />
              </a>
              <a
                href="https://www.linkedin.com/company/coolcaredrc/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-white/5 hover:bg-teal-500 flex items-center justify-center transition-all duration-300 hover:scale-110"
              >
                <LinkedinIcon className="w-4 h-4" />
              </a>
              <a
                href="https://instagram.com/coolcaredrc?igsh=MXI0c24wYm1jMWlrcA=="
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-white/5 hover:bg-teal-500 flex items-center justify-center transition-all duration-300 hover:scale-110"
              >
                <InstagramIcon className="w-4 h-4" />
              </a>
            </div>
          </div>

          <div className="lg:col-span-2">
            <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-teal-400 mb-5">{t('footerSection.solutionsTitle')}</h3>
            <ul className="space-y-3 text-sm text-white/40">
              {ourSolutionData.map((solution) => (
                <li key={solution.slug}>
                  <Link to={`/solutions/${solution.slug}`} className="hover:text-white transition-colors">
                    {t(`footerSection.solutionLabels.${solution.slug}`)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-teal-400 mb-5">{t('footerSection.companyTitle')}</h3>
            <ul className="space-y-3 text-sm text-white/40">
              <li><Link to="/about">{t('footerSection.companyLinks.about')}</Link></li>
              <li><Link to="/partners">{t('footerSection.companyLinks.partners')}</Link></li>
              <li><Link to="/industries">{t('footerSection.companyLinks.industries')}</Link></li>
              <li><Link to="/projects">{t('footerSection.companyLinks.projects')}</Link></li>
              <li><Link to="/contact">{t('footerSection.companyLinks.contact')}</Link></li>
            </ul>
          </div>

          <div className="lg:col-span-4">
            <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-teal-400 mb-5">{t('footerSection.contactTitle')}</h3>
            <ul className="space-y-4 text-sm text-white/40">
              <li className="flex items-start gap-3">
                <PhoneIcon className="mt-0.5 w-4 h-4 shrink-0 text-[#20d3cd]" />
                <div className="space-y-2">
                  {siteConfig.phoneNumbers.map((phoneNumber) => (
                    <a
                      key={`${siteConfig.key}-footer-phone-${phoneNumber}`}
                      href={`tel:${phoneNumber.replace(/\s+/g, '')}`}
                      className="block hover:text-white transition-colors"
                    >
                      {phoneNumber}
                    </a>
                  ))}
                </div>
              </li>
              <li className="flex items-start gap-3">
                <MailIcon className="mt-0.5 w-4 h-4 shrink-0 text-[#20d3cd]" />
                <div className="space-y-2">
                  {siteConfig.emails.map((emailAddress) => (
                    <a
                      key={`${siteConfig.key}-footer-email-${emailAddress}`}
                      href={`mailto:${emailAddress}`}
                      className="block hover:text-white transition-colors break-all"
                    >
                      {emailAddress}
                    </a>
                  ))}
                </div>
              </li>
              <li className="flex items-start gap-3">
                <MapIcon className="mt-1 w-4 h-4 shrink-0 text-[#20d3cd]" />
                <div>
                  <p className="text-white/60 font-medium">{siteConfig.footerLocation.primary}</p>
                  <p className="text-white/30 text-xs mt-0.5">{siteConfig.footerLocation.secondary}</p>
                  {siteConfig.footerLocation.tertiary && (
                    <p className="text-white/30 text-xs mt-0.5">{siteConfig.footerLocation.tertiary}</p>
                  )}
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-white/20">
          <p>{t('footerSection.copyright')}</p>
          <div className="flex gap-6">
            <Link to="/" className="hover:text-white/50 transition-colors">{t('footerSection.bottomLinks.home')}</Link>
            <Link to="/solutions" className="hover:text-white/50 transition-colors">{t('footerSection.bottomLinks.solutions')}</Link>
            <Link to="/partners" className="hover:text-white/50 transition-colors">{t('footerSection.bottomLinks.partners')}</Link>
            <Link to="/contact" className="hover:text-white/50 transition-colors">{t('footerSection.bottomLinks.contact')}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
